import React, { useMemo } from "react";
import {
  PaperPlaneTilt,
  CheckCircle,
  XCircle,
  CurrencyInr,
  Hourglass,
  GraduationCap,
  Tag,
} from "@phosphor-icons/react";

/**
 * Timeline — derives a chronological list of state transitions for a single
 * registration from existing audit fields. Pure UI; no API calls.
 */
export default function Timeline({ registration: reg }) {
  const events = useMemo(() => {
    const out = [];
    if (reg.submitted_at) {
      out.push({
        key: "submitted",
        icon: PaperPlaneTilt,
        color: "var(--sms-primary)",
        label: "Application submitted",
        at: reg.submitted_at,
      });
    }
    if (reg.decided_at && reg.status === "rejected") {
      out.push({
        key: "rejected",
        icon: XCircle,
        color: "var(--sms-danger)",
        label: `Rejected${reg.rejection_reason ? ` · ${reg.rejection_reason}` : ""}`,
        at: reg.decided_at,
      });
    }
    if (reg.decided_at && (reg.status === "approved" || reg.status === "enrolled")) {
      out.push({
        key: "approved",
        icon: CheckCircle,
        color: "var(--sms-primary)",
        label: `Approved · Student ID ${reg.student_id || ""}`,
        at: reg.decided_at,
      });
    }
    if (reg.payment_requested_at) {
      out.push({
        key: "payment-requested",
        icon: CurrencyInr,
        color: "var(--sms-primary)",
        label: "Payment link generated",
        at: reg.payment_requested_at,
      });
    }
    (reg.payment_history || []).forEach((h, i) => {
      out.push({
        key: `claim-${i}`,
        icon: Hourglass,
        color: "var(--sms-warning)",
        label: `UTR claimed · ${h.utr}${h.installment_number ? ` (instalment #${h.installment_number})` : ""}`,
        at: h.claimed_at,
      });
    });
    (reg.discount_history || []).forEach((h, i) => {
      const valueTxt =
        h.input_type === "percent"
          ? `${h.input_value}%`
          : `₹${Number(h.amount || 0).toLocaleString("en-IN")}`;
      let label;
      if (h.action === "removed") {
        label = `Discount removed by ${h.by_email}`;
      } else if (h.action === "updated") {
        label = `Discount updated to ${valueTxt} by ${h.by_email} · "${h.reason}"`;
      } else {
        label = `Discount applied (${valueTxt}) by ${h.by_email} · "${h.reason}"`;
      }
      out.push({
        key: `discount-${i}`,
        icon: Tag,
        color: "var(--sms-primary)",
        label,
        at: h.at,
      });
    });
    (reg.installments || []).forEach((it) => {
      if (it.verified_at) {
        out.push({
          key: `inst-verified-${it.number}`,
          icon: CheckCircle,
          color: "var(--sms-success)",
          label: `Instalment #${it.number} verified · ₹${Number(it.amount).toLocaleString("en-IN")}`,
          at: it.verified_at,
        });
      }
    });
    if (reg.enrolled_at) {
      out.push({
        key: "enrolled",
        icon: GraduationCap,
        color: "var(--sms-success)",
        label: "Enrolled",
        at: reg.enrolled_at,
      });
    }
    out.sort((a, b) => new Date(a.at) - new Date(b.at));
    return out;
  }, [reg]);

  if (events.length === 0) return null;

  return (
    <div className="border border-slate-200 p-5" data-testid="reg-timeline">
      <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)] mb-4">
        Timeline ({events.length})
      </div>
      <ol className="space-y-3 relative pl-6">
        <span className="absolute left-[9px] top-1.5 bottom-1.5 w-px bg-slate-200" aria-hidden />
        {events.map((e) => {
          const Icon = e.icon;
          return (
            <li key={e.key} className="relative flex items-start gap-3 sms-fade-up" data-testid={`timeline-${e.key}`}>
              <span
                className="absolute -left-[19px] top-0.5 w-4 h-4 flex items-center justify-center bg-white border"
                style={{ borderColor: e.color }}
              >
                <Icon size={10} weight="bold" style={{ color: e.color }} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm">{e.label}</div>
                <div className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-400 mt-0.5">
                  {new Date(e.at).toLocaleString()}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
